'use client';

import { c as _c } from "react/compiler-runtime";
import { buildSparseConvictionChart, convictionChartTerminalTo, convictionFraction, materializeSparseConvictionChart, resolveConvictionChartStart, selectConvictionChartInterval, useAuthoritativeConvictionChart, useGetMarketField, useGetTrades, useMarketConfig } from '@morfi/backend-sdk-v2/client';
import { domAnimation, LazyMotion, m, useReducedMotion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { type LiveTrade, OVERLAY_TRADE_FIELDS } from '@/components/markets/live-trades/live-trades-overlay';
import { tradesRowsToLiveTrades } from '@/components/markets/live-trades/tape-mapping';
import { buildOutcomeRowSummaries, type OutcomeRowSummary } from '@/components/markets/outcome-row-summary';
import { useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { useTimeframeChartPresentation } from '@/hooks/chart/use-timeframe-chart-presentation';
import { useIntersectionObserver } from '@/hooks/ui/use-intersection-observer';
import { useAutoScrollToLeader } from '@/hooks/use-auto-scroll-to-leader';
import { marketTradeLiveFrom } from '@/lib/sdk/trade-scan-boundary';
import { logger } from '@/lib/telemetry/logger';
import { MOTION_POSITION } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';
import type { HeroMarket } from './hero-market';
import { useHeroOutcomeTheses } from './hero-outcome-theses';
import type { OutcomeDecorParams } from './outcome-decor';
import type { OutcomeSparklinePoint } from './outcome-sparkline';
import { OutcomeSparklineCard } from './outcome-sparkline-card';
interface OutcomeSparklineGridProps {
  market: HeroMarket;
  slot?: number;
  className?: string;
  /** Workspace-only — forwarded to every card's decorative image override. */
  decorOverride?: OutcomeDecorParams;
}

// Resting (non-hover) window. Hovering a card expands it to the market's full
// history. Deliberately short: the backend returns buckets
// only from a market's first data point, so a window wider than the market's
// age makes hover a no-op (a 7-day default did exactly that on every
// sub-week-old market). 6h keeps the expand visible on young markets too.
const SPARKLINE_WINDOW_MS: number = 6 * 60 * 60 * 1000;
const SPARKLINE_WINDOW_SECS: number = SPARKLINE_WINDOW_MS / 1000;
/** Matches `market-row-card` scroll stack — three cards visible in the scrollport. */
const SCROLL_COLUMN_CLASS = 'flex min-h-0 min-w-0 flex-col self-stretch snap-start basis-[calc((100%-16px)/3)] shrink-0 grow-0';
const CARD_COLUMN_CLASS = 'flex min-h-0 min-w-0 flex-col self-stretch';
function sparklineToIso(): string {
  return new Date(Math.floor(Date.now() / 60_000) * 60_000).toISOString();
}
function restingWindowSecs(createdAtIso: string | undefined, toIso: string): number {
  if (createdAtIso === undefined) return SPARKLINE_WINDOW_SECS;
  const marketAgeSecs = Math.floor((Date.parse(toIso) - Date.parse(createdAtIso)) / 1000);
  if (!Number.isFinite(marketAgeSecs) || marketAgeSecs <= 0) return SPARKLINE_WINDOW_SECS;
  return Math.min(SPARKLINE_WINDOW_SECS, Math.max(60, marketAgeSecs));
}
interface SparklineCardLeafProps {
  marketPubkey: string;
  outcome: OutcomeRowSummary;
  colorClasses: ReturnType<typeof useOutcomeColor>[number];
  thesis: ReturnType<typeof useHeroOutcomeTheses>['thesesByOutcomeIndex'] extends Map<number, infer T> ? T | undefined : never;
  decimals: boolean;
  slot: number;
  /** Card's live-trade tape — mapped once by the grid, shared by every card. */
  liveTrades: readonly LiveTrade[];
  /** SDK-projected post-mount live trade ids (see `LiveTradesOverlay`). */
  liveSinceMountIds: readonly string[];
  /** Latest post-mount realtime trade for this outcome, or null when none exists. */
  degenTriggerId: string | null;
  chartData: ReadonlyArray<OutcomeSparklinePoint> | undefined;
  chartRequestKey: string;
  chartReady: boolean;
  expandedOutcomeIndex: number | null;
  expandedWindowSecs: number;
  onExpandedOutcomeChange: (outcomeIndex: number | null) => void;
  decorOverride?: OutcomeDecorParams;
}
function latestLiveTradeIdsByOutcome(trades: readonly LiveTrade[], liveSinceMountIds: readonly string[]): ReadonlyMap<number, string> {
  const tradeById = new Map(trades.map(trade => [trade.id, trade]));
  const latestByOutcome = new Map<number, string>();
  // The SDK projection is insertion-ordered oldest → newest, so later writes
  // intentionally replace each outcome's prior trigger.
  for (const id of liveSinceMountIds) {
    const trade = tradeById.get(id);
    if (trade !== undefined) latestByOutcome.set(trade.outcomeIndex, id);
  }
  return latestByOutcome;
}
function SparklineCardWithChart(props) {
  const $ = _c(37);
  const [hovered, setHovered] = useState(false);
  const [outgoingData, setOutgoingData] = useState(null);
  const [exitWindowTransitionComplete, setExitWindowTransitionComplete] = useState(false);
  const expanded = hovered && props.expandedOutcomeIndex === props.outcome.originalIndex;
  const restingWindowSecs = Math.min(SPARKLINE_WINDOW_SECS, props.expandedWindowSecs);
  const windowSecs = expanded ? props.expandedWindowSecs : restingWindowSecs;
  const t0 = `${props.marketPubkey}:${String(props.outcome.originalIndex)}:conviction`;
  let t1;
  if ($[0] !== props.chartData) {
    t1 = props.chartData ?? [];
    $[0] = props.chartData;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== props.chartReady || $[3] !== props.chartRequestKey || $[4] !== t0 || $[5] !== t1 || $[6] !== windowSecs) {
    t2 = {
      seriesKey: t0,
      requestKey: props.chartRequestKey,
      data: t1,
      targetReady: props.chartReady,
      windowSecs
    };
    $[2] = props.chartReady;
    $[3] = props.chartRequestKey;
    $[4] = t0;
    $[5] = t1;
    $[6] = windowSecs;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const presentation = useTimeframeChartPresentation(t2);
  const keepOutgoingData = outgoingData !== null && (!exitWindowTransitionComplete || presentation.isTargetLoading);
  const displayData = keepOutgoingData ? outgoingData : presentation.data;
  const displayWindowSecs = outgoingData === null ? presentation.windowSecs : restingWindowSecs;
  let t3;
  if ($[8] !== presentation || $[9] !== props || $[10] !== restingWindowSecs) {
    t3 = nextHovered => {
      if (nextHovered) {
        setOutgoingData(null);
        setExitWindowTransitionComplete(false);
        setHovered(true);
        props.onExpandedOutcomeChange(props.outcome.originalIndex);
        return;
      }
      if (presentation.data !== null && presentation.windowSecs > restingWindowSecs) {
        setOutgoingData(presentation.data);
        setExitWindowTransitionComplete(false);
      }
      setHovered(false);
      props.onExpandedOutcomeChange(null);
    };
    $[8] = presentation;
    $[9] = props;
    $[10] = restingWindowSecs;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const handleHoverChange = t3;
  let t4;
  if ($[12] !== outgoingData || $[13] !== restingWindowSecs) {
    t4 = completedWindowSecs => {
      if (outgoingData === null || completedWindowSecs !== restingWindowSecs) {
        return;
      }
      setExitWindowTransitionComplete(true);
    };
    $[12] = outgoingData;
    $[13] = restingWindowSecs;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const handleWindowTransitionComplete = t4;
  const t5 = props.outcome.imageUrl ?? null;
  const t6 = props.outcome.imageNsfw ?? false;
  const t7 = displayData ?? undefined;
  let t8;
  if ($[15] !== props.decorOverride) {
    t8 = props.decorOverride !== undefined ? {
      decorOverride: props.decorOverride
    } : {};
    $[15] = props.decorOverride;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== displayWindowSecs || $[18] !== handleHoverChange || $[19] !== handleWindowTransitionComplete || $[20] !== props.colorClasses || $[21] !== props.decimals || $[22] !== props.degenTriggerId || $[23] !== props.liveSinceMountIds || $[24] !== props.liveTrades || $[25] !== props.marketPubkey || $[26] !== props.outcome.effectiveMarketCapD6 || $[27] !== props.outcome.label || $[28] !== props.outcome.originalIndex || $[29] !== props.outcome.share || $[30] !== props.slot || $[31] !== props.thesis || $[32] !== t5 || $[33] !== t6 || $[34] !== t7 || $[35] !== t8) {
    t9 = <OutcomeSparklineCard marketPubkey={props.marketPubkey} outcomeIndex={props.outcome.originalIndex} outcomeLabel={props.outcome.label} share={props.outcome.share} effectiveMarketCapD6={props.outcome.effectiveMarketCapD6} colorClasses={props.colorClasses} imageUrl={t5} imageNsfw={t6} data={t7} degenTriggerId={props.degenTriggerId} timeWindowSecs={displayWindowSecs} thesis={props.thesis} decimals={props.decimals} slot={props.slot} trades={props.liveTrades} liveSinceMountIds={props.liveSinceMountIds} onHoverChange={handleHoverChange} onWindowTransitionComplete={handleWindowTransitionComplete} {...t8} />;
    $[17] = displayWindowSecs;
    $[18] = handleHoverChange;
    $[19] = handleWindowTransitionComplete;
    $[20] = props.colorClasses;
    $[21] = props.decimals;
    $[22] = props.degenTriggerId;
    $[23] = props.liveSinceMountIds;
    $[24] = props.liveTrades;
    $[25] = props.marketPubkey;
    $[26] = props.outcome.effectiveMarketCapD6;
    $[27] = props.outcome.label;
    $[28] = props.outcome.originalIndex;
    $[29] = props.outcome.share;
    $[30] = props.slot;
    $[31] = props.thesis;
    $[32] = t5;
    $[33] = t6;
    $[34] = t7;
    $[35] = t8;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  return t9;
}
function SparklineCardLeaf(props) {
  const $ = _c(27);
  let cardProps;
  let deferChartUntilVisible;
  if ($[0] !== props) {
    ({
      deferChartUntilVisible,
      ...cardProps
    } = props);
    $[0] = props;
    $[1] = cardProps;
    $[2] = deferChartUntilVisible;
  } else {
    cardProps = $[1];
    deferChartUntilVisible = $[2];
  }
  const cardRef = useRef(null);
  let t0;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      rootMargin: "0px 200px",
      once: true
    };
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const {
    isIntersecting: chartMounted
  } = useIntersectionObserver(cardRef, t0);
  if (!deferChartUntilVisible || chartMounted) {
    let t1;
    if ($[4] !== cardProps) {
      t1 = <SparklineCardWithChart {...cardProps} />;
      $[4] = cardProps;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    return t1;
  }
  const t1 = `sparkline-chart-gate-${String(cardProps.outcome.originalIndex)}`;
  const t2 = cardProps.outcome.imageUrl ?? null;
  const t3 = cardProps.outcome.imageNsfw ?? false;
  let t4;
  if ($[6] !== cardProps.decorOverride) {
    t4 = cardProps.decorOverride !== undefined ? {
      decorOverride: cardProps.decorOverride
    } : {};
    $[6] = cardProps.decorOverride;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== cardProps.colorClasses || $[9] !== cardProps.decimals || $[10] !== cardProps.degenTriggerId || $[11] !== cardProps.liveSinceMountIds || $[12] !== cardProps.liveTrades || $[13] !== cardProps.marketPubkey || $[14] !== cardProps.outcome.effectiveMarketCapD6 || $[15] !== cardProps.outcome.label || $[16] !== cardProps.outcome.originalIndex || $[17] !== cardProps.outcome.share || $[18] !== cardProps.slot || $[19] !== cardProps.thesis || $[20] !== t2 || $[21] !== t3 || $[22] !== t4) {
    t5 = <OutcomeSparklineCard marketPubkey={cardProps.marketPubkey} outcomeIndex={cardProps.outcome.originalIndex} outcomeLabel={cardProps.outcome.label} share={cardProps.outcome.share} effectiveMarketCapD6={cardProps.outcome.effectiveMarketCapD6} colorClasses={cardProps.colorClasses} imageUrl={t2} imageNsfw={t3} data={undefined} degenTriggerId={cardProps.degenTriggerId} thesis={cardProps.thesis} decimals={cardProps.decimals} slot={cardProps.slot} trades={cardProps.liveTrades} liveSinceMountIds={cardProps.liveSinceMountIds} {...t4} />;
    $[8] = cardProps.colorClasses;
    $[9] = cardProps.decimals;
    $[10] = cardProps.degenTriggerId;
    $[11] = cardProps.liveSinceMountIds;
    $[12] = cardProps.liveTrades;
    $[13] = cardProps.marketPubkey;
    $[14] = cardProps.outcome.effectiveMarketCapD6;
    $[15] = cardProps.outcome.label;
    $[16] = cardProps.outcome.originalIndex;
    $[17] = cardProps.outcome.share;
    $[18] = cardProps.slot;
    $[19] = cardProps.thesis;
    $[20] = t2;
    $[21] = t3;
    $[22] = t4;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  let t6;
  if ($[24] !== t1 || $[25] !== t5) {
    t6 = <div ref={cardRef} className="flex min-h-0 flex-1" data-testid={t1}>{t5}</div>;
    $[24] = t1;
    $[25] = t5;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  return t6;
}
interface SparklineGridContentProps {
  readonly marketPubkey: string;
  readonly displayed: ReadonlyArray<OutcomeRowSummary>;
  readonly summaries: ReadonlyArray<OutcomeRowSummary>;
  readonly colors: ReturnType<typeof useOutcomeColor>;
  readonly thesesByOutcomeIndex: ReturnType<typeof useHeroOutcomeTheses>['thesesByOutcomeIndex'];
  readonly useScrollMode: boolean;
  readonly containerClass: string;
  readonly scrollStackRef: React.RefObject<HTMLDivElement | null>;
  readonly slot: number;
  readonly liveTrades: readonly LiveTrade[];
  readonly liveSinceMountIds: readonly string[];
  readonly degenTriggerIds: ReadonlyMap<number, string>;
  readonly decimals: boolean;
  readonly reduceMotion: boolean;
  readonly chartData: ReadonlyMap<number, ReadonlyArray<OutcomeSparklinePoint>>;
  readonly chartRequestKey: string;
  readonly chartReady: boolean;
  readonly expandedOutcomeIndex: number | null;
  readonly expandedWindowSecs: number;
  readonly onExpandedOutcomeChange: (outcomeIndex: number | null) => void;
  readonly decorOverride?: OutcomeDecorParams;
}
function SparklineGridContent(props) {
  const $ = _c(44);
  const t0 = props.useScrollMode ? props.scrollStackRef : undefined;
  const t1 = props.containerClass;
  const t2 = props.useScrollMode ? "true" : "false";
  let t3;
  if ($[0] !== props.chartData || $[1] !== props.chartReady || $[2] !== props.chartRequestKey || $[3] !== props.colors || $[4] !== props.decimals || $[5] !== props.decorOverride || $[6] !== props.degenTriggerIds || $[7] !== props.displayed || $[8] !== props.expandedOutcomeIndex || $[9] !== props.expandedWindowSecs || $[10] !== props.liveSinceMountIds || $[11] !== props.liveTrades || $[12] !== props.marketPubkey || $[13] !== props.onExpandedOutcomeChange || $[14] !== props.reduceMotion || $[15] !== props.slot || $[16] !== props.summaries || $[17] !== props.thesesByOutcomeIndex || $[18] !== props.useScrollMode) {
    let t4;
    if ($[20] !== props.chartData || $[21] !== props.chartReady || $[22] !== props.chartRequestKey || $[23] !== props.colors || $[24] !== props.decimals || $[25] !== props.decorOverride || $[26] !== props.degenTriggerIds || $[27] !== props.expandedOutcomeIndex || $[28] !== props.expandedWindowSecs || $[29] !== props.liveSinceMountIds || $[30] !== props.liveTrades || $[31] !== props.marketPubkey || $[32] !== props.onExpandedOutcomeChange || $[33] !== props.reduceMotion || $[34] !== props.slot || $[35] !== props.summaries || $[36] !== props.thesesByOutcomeIndex || $[37] !== props.useScrollMode) {
      t4 = outcome => {
        const inputPosition = props.summaries.findIndex(candidate => candidate.originalIndex === outcome.originalIndex);
        const colors = inputPosition === -1 ? undefined : props.colors[inputPosition];
        if (colors === undefined) {
          return null;
        }
        return <m.div key={outcome.originalIndex} className={cn(CARD_COLUMN_CLASS, props.useScrollMode && SCROLL_COLUMN_CLASS)} layout={props.reduceMotion ? false : "position"} transition={props.reduceMotion ? {
          duration: 0
        } : MOTION_POSITION}><SparklineCardLeaf marketPubkey={props.marketPubkey} outcome={outcome} colorClasses={colors} thesis={props.thesesByOutcomeIndex.get(outcome.originalIndex)} decimals={props.decimals} slot={props.slot} liveTrades={props.liveTrades} liveSinceMountIds={props.liveSinceMountIds} degenTriggerId={props.degenTriggerIds.get(outcome.originalIndex) ?? null} chartData={props.chartData.get(outcome.originalIndex)} chartRequestKey={props.chartRequestKey} chartReady={props.chartReady} expandedOutcomeIndex={props.expandedOutcomeIndex} expandedWindowSecs={props.expandedWindowSecs} onExpandedOutcomeChange={props.onExpandedOutcomeChange} deferChartUntilVisible={props.useScrollMode} {...props.decorOverride === undefined ? {} : {
            decorOverride: props.decorOverride
          }} /></m.div>;
      };
      $[20] = props.chartData;
      $[21] = props.chartReady;
      $[22] = props.chartRequestKey;
      $[23] = props.colors;
      $[24] = props.decimals;
      $[25] = props.decorOverride;
      $[26] = props.degenTriggerIds;
      $[27] = props.expandedOutcomeIndex;
      $[28] = props.expandedWindowSecs;
      $[29] = props.liveSinceMountIds;
      $[30] = props.liveTrades;
      $[31] = props.marketPubkey;
      $[32] = props.onExpandedOutcomeChange;
      $[33] = props.reduceMotion;
      $[34] = props.slot;
      $[35] = props.summaries;
      $[36] = props.thesesByOutcomeIndex;
      $[37] = props.useScrollMode;
      $[38] = t4;
    } else {
      t4 = $[38];
    }
    t3 = props.displayed.map(t4);
    $[0] = props.chartData;
    $[1] = props.chartReady;
    $[2] = props.chartRequestKey;
    $[3] = props.colors;
    $[4] = props.decimals;
    $[5] = props.decorOverride;
    $[6] = props.degenTriggerIds;
    $[7] = props.displayed;
    $[8] = props.expandedOutcomeIndex;
    $[9] = props.expandedWindowSecs;
    $[10] = props.liveSinceMountIds;
    $[11] = props.liveTrades;
    $[12] = props.marketPubkey;
    $[13] = props.onExpandedOutcomeChange;
    $[14] = props.reduceMotion;
    $[15] = props.slot;
    $[16] = props.summaries;
    $[17] = props.thesesByOutcomeIndex;
    $[18] = props.useScrollMode;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  let t4;
  if ($[39] !== props.containerClass || $[40] !== t0 || $[41] !== t2 || $[42] !== t3) {
    t4 = <LazyMotion features={domAnimation}><div ref={t0} className={t1} data-testid="outcome-sparkline-grid" data-scroll-mode={t2}>{t3}</div></LazyMotion>;
    $[39] = props.containerClass;
    $[40] = t0;
    $[41] = t2;
    $[42] = t3;
    $[43] = t4;
  } else {
    t4 = $[43];
  }
  return t4;
}
interface SparklineGridHistoryOwnerProps extends Omit<SparklineGridContentProps, 'chartData' | 'chartRequestKey' | 'chartReady' | 'expandedOutcomeIndex' | 'expandedWindowSecs' | 'onExpandedOutcomeChange'> {
  readonly tradingStartedAt: string;
  readonly resolvedAt: string | null;
  readonly outcomeCount: number;
  readonly retentionPerSecondPpb: string;
}
function SparklineGridHistoryOwner(props) {
  const $ = _c(39);
  const [expandedOutcomeIndex, setExpandedOutcomeIndex] = useState(null);
  const [head] = useState(sparklineToIso);
  const viewportTo = new Date(Date.parse(head) + 60000).toISOString();
  let from;
  let t0;
  let terminalTo;
  if ($[0] !== expandedOutcomeIndex || $[1] !== head || $[2] !== props.resolvedAt || $[3] !== props.tradingStartedAt) {
    from = resolveConvictionChartStart({
      tradingStartedAt: props.tradingStartedAt,
      head,
      ...(expandedOutcomeIndex === null ? {
        lookbackSeconds: SPARKLINE_WINDOW_SECS
      } : {})
    });
    let t1;
    if ($[7] !== props.resolvedAt) {
      t1 = props.resolvedAt === null ? undefined : convictionChartTerminalTo(props.resolvedAt);
      $[7] = props.resolvedAt;
      $[8] = t1;
    } else {
      t1 = $[8];
    }
    terminalTo = t1;
    t0 = selectConvictionChartInterval(from, terminalTo ?? head);
    $[0] = expandedOutcomeIndex;
    $[1] = head;
    $[2] = props.resolvedAt;
    $[3] = props.tradingStartedAt;
    $[4] = from;
    $[5] = t0;
    $[6] = terminalTo;
  } else {
    from = $[4];
    t0 = $[5];
    terminalTo = $[6];
  }
  const interval = t0;
  const t1 = expandedOutcomeIndex === null ? "sparkline" : "expanded";
  let t2;
  if ($[9] !== terminalTo) {
    t2 = terminalTo === undefined ? {} : {
      to: terminalTo
    };
    $[9] = terminalTo;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== from || $[12] !== head || $[13] !== interval || $[14] !== props.marketPubkey || $[15] !== t1 || $[16] !== t2) {
    t3 = {
      pubkey: props.marketPubkey,
      selectionKey: t1,
      interval,
      from,
      head,
      limit: 360,
      ...t2
    };
    $[11] = from;
    $[12] = head;
    $[13] = interval;
    $[14] = props.marketPubkey;
    $[15] = t1;
    $[16] = t2;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  const query = useAuthoritativeConvictionChart(t3);
  let chart;
  let chartData;
  if ($[18] !== props.outcomeCount || $[19] !== props.resolvedAt || $[20] !== props.retentionPerSecondPpb || $[21] !== props.tradingStartedAt || $[22] !== query.data) {
    chart = query.data === undefined || query.data === null ? null : buildSparseConvictionChart({
      snapshot: query.data,
      tradingStartedAt: props.tradingStartedAt,
      outcomeCount: props.outcomeCount,
      retentionPerSecondPpb: props.retentionPerSecondPpb,
      resolvedAt: props.resolvedAt
    });
    const samples = chart === null ? [] : materializeSparseConvictionChart(chart);
    chartData = new Map();
    for (let outcomeIndex = 0; outcomeIndex < props.outcomeCount; outcomeIndex = outcomeIndex + 1, outcomeIndex) {
      const data = samples.flatMap(sample => {
        const valuePpb = sample.valuesPpb[outcomeIndex];
        return valuePpb === undefined ? [] : [{
          time: sample.timeMs / 1000,
          value: convictionFraction(valuePpb)
        }];
      });
      chartData.set(outcomeIndex, data);
    }
    $[18] = props.outcomeCount;
    $[19] = props.resolvedAt;
    $[20] = props.retentionPerSecondPpb;
    $[21] = props.tradingStartedAt;
    $[22] = query.data;
    $[23] = chart;
    $[24] = chartData;
  } else {
    chart = $[23];
    chartData = $[24];
  }
  const requestKey = `${interval}:${from}:${props.resolvedAt ?? "latest"}`;
  const expandedWindowSecs = chart === null || expandedOutcomeIndex === null ? restingWindowSecs(props.tradingStartedAt, viewportTo) : Math.max(60, Math.floor((chart.effectiveToMs - chart.fromMs) / 1000));
  let t4;
  let t5;
  if ($[25] !== from || $[26] !== interval || $[27] !== props.marketPubkey || $[28] !== props.resolvedAt || $[29] !== query.error) {
    t4 = () => {
      if (query.error === null) {
        return;
      }
      logger.warn("market.hero.conviction_chart_load_failed", {
        marketPubkey: props.marketPubkey,
        interval,
        from,
        to: props.resolvedAt
      });
    };
    t5 = [from, interval, props.marketPubkey, props.resolvedAt, query.error];
    $[25] = from;
    $[26] = interval;
    $[27] = props.marketPubkey;
    $[28] = props.resolvedAt;
    $[29] = query.error;
    $[30] = t4;
    $[31] = t5;
  } else {
    t4 = $[30];
    t5 = $[31];
  }
  useEffect(t4, t5);
  const t6 = query.data !== undefined && query.error === null;
  let t7;
  if ($[32] !== chartData || $[33] !== expandedOutcomeIndex || $[34] !== expandedWindowSecs || $[35] !== props || $[36] !== requestKey || $[37] !== t6) {
    t7 = <SparklineGridContent {...props} chartData={chartData} chartRequestKey={requestKey} chartReady={t6} expandedOutcomeIndex={expandedOutcomeIndex} expandedWindowSecs={expandedWindowSecs} onExpandedOutcomeChange={setExpandedOutcomeIndex} />;
    $[32] = chartData;
    $[33] = expandedOutcomeIndex;
    $[34] = expandedWindowSecs;
    $[35] = props;
    $[36] = requestKey;
    $[37] = t6;
    $[38] = t7;
  } else {
    t7 = $[38];
  }
  return t7;
}
const EMPTY_SPARKLINE_DATA: ReadonlyMap<number, ReadonlyArray<OutcomeSparklinePoint>> = new Map();
const ignoreExpandedOutcomeChange = (_outcomeIndex: number | null): void => {};
export function OutcomeSparklineGrid(props) {
  const $ = _c(47);
  const {
    market,
    slot,
    className,
    decorOverride
  } = props;
  const reduceMotion = useReducedMotion();
  const [tradesFrom] = useState(_temp);
  const liveOutcomes = useGetMarketField({
    pubkey: market.pubkey
  }, "outcomes") ?? market.outcomes;
  const status = useGetMarketField({
    pubkey: market.pubkey
  }, "status") ?? market.status;
  let t0;
  if ($[0] !== market.pubkey) {
    t0 = {
      pubkey: market.pubkey
    };
    $[0] = market.pubkey;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const resolvedAt = useGetMarketField(t0, "resolvedAt");
  let t1;
  if ($[2] !== market.pubkey) {
    t1 = {
      pubkey: market.pubkey
    };
    $[2] = market.pubkey;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const tradingStartedAt = useGetMarketField(t1, "tradingStartedAt");
  const convictionUpdatedAt = useGetMarketField({
    pubkey: market.pubkey
  }, "convictionUpdatedAt") ?? market.convictionUpdatedAt;
  const outcomeCount = useGetMarketField({
    pubkey: market.pubkey
  }, "outcomeCount") ?? market.outcomeCount;
  const marketConfig = useMarketConfig(market.marketConfig);
  const {
    thesesByOutcomeIndex
  } = useHeroOutcomeTheses(market.pubkey);
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [...OVERLAY_TRADE_FIELDS];
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== market.pubkey || $[6] !== tradesFrom) {
    t3 = {
      market: market.pubkey,
      from: tradesFrom,
      fields: t2
    };
    $[5] = market.pubkey;
    $[6] = tradesFrom;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const tradesQuery = useGetTrades(t3);
  let liveTrades;
  let t4;
  if ($[8] !== tradesQuery.ids || $[9] !== tradesQuery.liveSinceMountIds || $[10] !== tradesQuery.rows) {
    liveTrades = tradesRowsToLiveTrades(tradesQuery.rows, tradesQuery.ids);
    t4 = latestLiveTradeIdsByOutcome(liveTrades, tradesQuery.liveSinceMountIds);
    $[8] = tradesQuery.ids;
    $[9] = tradesQuery.liveSinceMountIds;
    $[10] = tradesQuery.rows;
    $[11] = liveTrades;
    $[12] = t4;
  } else {
    liveTrades = $[11];
    t4 = $[12];
  }
  const degenTriggerIds = t4;
  const summaries = buildOutcomeRowSummaries(liveOutcomes, {
    status,
    resolvedAt: resolvedAt === undefined ? market.resolvedAt : resolvedAt,
    convictionUpdatedAt,
    outcomeCount
  });
  const displayed = summaries.toSorted(_temp2);
  const useScrollMode = displayed.length > 3;
  const scrollStackRef = useRef(null);
  const leaderOriginalIndex = displayed[0]?.originalIndex ?? -1;
  let t5;
  if ($[13] !== useScrollMode) {
    t5 = {
      enabled: useScrollMode
    };
    $[13] = useScrollMode;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  useAutoScrollToLeader(scrollStackRef, leaderOriginalIndex, t5);
  const shareSum = displayed.reduce(_temp3, 0);
  const showDecimals = shareSum > 0;
  const allColorOutcomes = summaries.map(_temp4);
  let t6;
  if ($[15] !== market.pubkey) {
    t6 = {
      marketPubkey: market.pubkey
    };
    $[15] = market.pubkey;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const allColors = useOutcomeColor(allColorOutcomes, t6);
  const count = displayed.length;
  let t7;
  bb0: {
    if (count === 1) {
      t7 = "grid grid-cols-1 items-stretch gap-2";
      break bb0;
    }
    if (count === 2) {
      t7 = "grid grid-cols-2 items-stretch gap-2";
      break bb0;
    }
    t7 = "grid grid-cols-3 items-stretch gap-2";
  }
  const gridClass = t7;
  let t8;
  if ($[17] !== className || $[18] !== gridClass || $[19] !== useScrollMode) {
    t8 = useScrollMode ? cn("flex min-h-0 w-full min-w-0 items-stretch gap-2 overflow-x-auto overscroll-x-contain snap-x snap-mandatory scrollbar-none motion-reduce:scroll-auto", className) : cn(gridClass, className);
    $[17] = className;
    $[18] = gridClass;
    $[19] = useScrollMode;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const containerClass = t8;
  const t9 = slot ?? 0;
  const t10 = reduceMotion === true;
  let t11;
  if ($[21] !== decorOverride) {
    t11 = decorOverride === undefined ? {} : {
      decorOverride
    };
    $[21] = decorOverride;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== allColors || $[24] !== containerClass || $[25] !== degenTriggerIds || $[26] !== displayed || $[27] !== liveTrades || $[28] !== market.pubkey || $[29] !== showDecimals || $[30] !== summaries || $[31] !== t10 || $[32] !== t11 || $[33] !== t9 || $[34] !== thesesByOutcomeIndex || $[35] !== tradesQuery.liveSinceMountIds || $[36] !== useScrollMode) {
    t12 = {
      marketPubkey: market.pubkey,
      displayed,
      summaries,
      colors: allColors,
      thesesByOutcomeIndex,
      useScrollMode,
      containerClass,
      scrollStackRef,
      slot: t9,
      liveTrades,
      liveSinceMountIds: tradesQuery.liveSinceMountIds,
      degenTriggerIds,
      decimals: showDecimals,
      reduceMotion: t10,
      ...t11
    };
    $[23] = allColors;
    $[24] = containerClass;
    $[25] = degenTriggerIds;
    $[26] = displayed;
    $[27] = liveTrades;
    $[28] = market.pubkey;
    $[29] = showDecimals;
    $[30] = summaries;
    $[31] = t10;
    $[32] = t11;
    $[33] = t9;
    $[34] = thesesByOutcomeIndex;
    $[35] = tradesQuery.liveSinceMountIds;
    $[36] = useScrollMode;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  const content = t12;
  if (tradingStartedAt === undefined || tradingStartedAt === null || marketConfig === undefined) {
    const t13 = `${market.pubkey}:conviction-unavailable`;
    let t14;
    if ($[38] !== content || $[39] !== t13) {
      t14 = <SparklineGridContent {...content} chartData={EMPTY_SPARKLINE_DATA} chartRequestKey={t13} chartReady={true} expandedOutcomeIndex={null} expandedWindowSecs={SPARKLINE_WINDOW_SECS} onExpandedOutcomeChange={ignoreExpandedOutcomeChange} />;
      $[38] = content;
      $[39] = t13;
      $[40] = t14;
    } else {
      t14 = $[40];
    }
    return t14;
  }
  const t13 = resolvedAt === undefined ? market.resolvedAt : resolvedAt;
  let t14;
  if ($[41] !== content || $[42] !== marketConfig.convictionRetentionPerSecondPpb || $[43] !== outcomeCount || $[44] !== t13 || $[45] !== tradingStartedAt) {
    t14 = <SparklineGridHistoryOwner {...content} tradingStartedAt={tradingStartedAt} resolvedAt={t13} outcomeCount={outcomeCount} retentionPerSecondPpb={marketConfig.convictionRetentionPerSecondPpb} />;
    $[41] = content;
    $[42] = marketConfig.convictionRetentionPerSecondPpb;
    $[43] = outcomeCount;
    $[44] = t13;
    $[45] = tradingStartedAt;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  return t14;
}
function _temp4(o_0) {
  return {
    title: o_0.label,
    index: o_0.originalIndex
  };
}
function _temp3(sum, o) {
  return sum + o.share;
}
function _temp2(a, b) {
  return b.share - a.share;
}
function _temp() {
  return marketTradeLiveFrom();
}